import React, { useState, useEffect, useRef } from 'react';
import { navigate } from 'gatsby';
import { setItemToLStore } from '../helpers/hash';
import InputMask from 'react-input-mask';
import { useLocalStorage } from '../hooks/useLocalStorage';
import Button from './atoms/Button';
import { ArrowRight } from 'lucide-react';
import { trackFormSubmission } from '../helpers/analytics';

const FORMINIT_FORM_ID = 'yg5ybujubjr';

// Deliberately permissive, and deliberately free of nested quantifiers.
// The previous pattern — /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/ — did two
// things wrong: it capped the TLD at three characters (so .info, .online, .company
// and every other modern TLD bounced), and its `(...)*` inside `\w+` backtracked
// catastrophically whenever the address failed to match. A real address like
// jennifer@grandrapidsproperties.construction froze the tab for minutes instead of
// showing an error. Each class below excludes '@' and whitespace, so the match
// point is unambiguous and the whole thing runs in linear time.
// FormIt validates the address server-side too; this only needs to catch typos.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;

function ContactForm({
  hideLabels,
  showPlaceholders,
  stacked,
  fontsize,
  footerContained,
  grayInputBg = true,
  formLocation = 'inline',
}) {
  const [offer, setOffer] = useLocalStorage('offers', {});
  const [formInputStates, setFormInputStates] = useState({});
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const [formSubmitErrors, setFormSubmitErrors] = useState([]);
  const [utmParams, setUtmParams] = useState({});
  const [sdkLoaded, setSdkLoaded] = useState(false);
  // Guards against a second click landing before navigate('/thanks/') runs,
  // which would send a duplicate lead and a duplicate conversion event. State
  // updates are async, so this has to be a ref to be read back synchronously.
  const submitLock = useRef(false);

  useEffect(() => {
    // Parse UTM parameters from URL
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const utms = {};
      ['utm_content', 'utm_medium', 'utm_source', 'utm_term'].forEach((key) => {
        const val = params.get(key);
        if (val) utms[key] = val;
      });
      setUtmParams(utms);
    }

    // Load FormIt IN SDK
    if (typeof document !== 'undefined' && !document.querySelector('script[src*="forminit.com"]')) {
      const script = document.createElement('script');
      script.src = 'https://forminit.com/sdk/v1/forminit.js';
      script.async = true;
      script.onload = () => setSdkLoaded(true);
      document.head.appendChild(script);
    } else if (typeof window !== 'undefined' && window.Forminit) {
      setSdkLoaded(true);
    }
  }, []);

  function handleChange(e) {
    setFormInputStates({ ...formInputStates, [e.target.name]: e.target.value });
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (submitLock.current) return;
    submitLock.current = true;

    setSubmitInProgress(true);

    if (!validateForm()) {
      window.scroll(0, 0);
      setSubmitInProgress(false);
      submitLock.current = false;
      return;
    }

    // Format phone to E.164
    const rawPhone = (formInputStates.phone || '').replace(/\D/g, '');
    const e164Phone = rawPhone.length === 10 ? `+1${rawPhone}` : `+${rawPhone}`;

    // FormIt SDK expects a FormData object whose keys match the form's
    // configured blocks (Sender, Message, CurrentPath, LandingURL).
    const formData = new FormData();
    formData.append('fi-sender-firstName', formInputStates.Firstname || '');
    formData.append('fi-sender-lastName', formInputStates.Lastname || '');
    formData.append('fi-sender-email', (formInputStates.email || '').trim());
    formData.append('fi-sender-phone', e164Phone);
    formData.append('fi-text-message', formInputStates.description || '');

    // Optional tracking — only send when the value is real, otherwise the
    // URL/text block parser inside the SDK can choke on placeholder data.
    const ls = typeof window !== 'undefined' ? window.localStorage : null;
    const storedLandingURL = ls && ls.getItem('lNdUrL');
    const storedCurrentPath = ls && ls.getItem('crNtPaTH');
    if (storedLandingURL && /^https?:\/\//i.test(storedLandingURL)) {
      formData.append('fi-url-landingURL', storedLandingURL);
    }
    if (storedCurrentPath) {
      formData.append('fi-text-currentPath', storedCurrentPath);
    }

    const showError = (message) => {
      setFormSubmitErrors([
        `We couldn't submit your request: ${message}. Please call (269) 420-6623 or try again in a moment.`,
      ]);
      window.scroll(0, 0);
      setSubmitInProgress(false);
      submitLock.current = false;
    };

    if (typeof window === 'undefined' || !window.Forminit) {
      showError('Form service is still loading. Please try again in a moment');
      return;
    }

    try {
      const forminit = new window.Forminit();
      // SDK resolves with { data, error } — it does NOT reject on logical errors.
      const result = await forminit.submit(FORMINIT_FORM_ID, formData);

      if (result && result.error) {
        console.error('FormIt submission error:', result.error);
        showError(result.error.message || 'Unknown error');
        return;
      }

      setItemToLStore('form', { date: new Date(), ...formInputStates });

      // Only reached once FormIt has accepted the lead, so the conversion count
      // tracks real leads rather than submit attempts. Pushed before the
      // navigate() below — Gatsby routes client-side, so the page never unloads
      // and GTM has time to fire its tags either way.
      trackFormSubmission({
        formName: 'contact',
        formId: FORMINIT_FORM_ID,
        formLocation,
      });

      setTimeout(() => navigate('/thanks/'), 300);
    } catch (error) {
      console.error('FormIt submission error:', error);
      showError((error && (error.message || error.toString())) || 'Unknown error');
    }
  }

  function validateForm() {
    let sanitzePhone = (formInputStates.phone || '').replace(/_/g, '');
    let formValidateSuccess = true;
    let listOfErrors = [];

    if (!formInputStates.Firstname || formInputStates.Firstname.length < 2 || !formInputStates.Firstname.replace(/\s/g, '').length) {
      listOfErrors.push('First name is a required field.');
      formValidateSuccess = false;
    }
    if (!formInputStates.Lastname || formInputStates.Lastname.length < 2 || !formInputStates.Lastname.replace(/\s/g, '').length) {
      listOfErrors.push('Last name is a required field.');
      formValidateSuccess = false;
    }
    if (sanitzePhone.length - 4 < 10) {
      listOfErrors.push('Please enter a complete phone number.');
      formValidateSuccess = false;
    }
    if (!formInputStates.email || !formInputStates.email.trim()) {
      listOfErrors.push('Email is a required field.');
      formValidateSuccess = false;
    } else if (!EMAIL_PATTERN.test(formInputStates.email.trim())) {
      listOfErrors.push('Please enter a valid email address.');
      formValidateSuccess = false;
    }
    if (!formInputStates.description || formInputStates.description.length < 1 || !formInputStates.description.replace(/\s/g, '').length) {
      listOfErrors.push('Description cannot be left blank.');
      formValidateSuccess = false;
    }

    setFormSubmitErrors(listOfErrors);
    return formValidateSuccess;
  }

  const inputClasses = `${
    grayInputBg ? 'bg-neutral-50' : 'bg-white shadow-sm'
  } w-full input p-3 rounded-lg border-neutral-100 border appearance-none focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-colors`;

  return (
    <form
      name="contact"
      method="post"
      action="/thanks/"
      onSubmit={(e) => handleSubmit(e)}
    >
      {offer && Object.keys(offer).length > 0 ? (
        <div className="shadow-md bg-white border-l-4 border-brand-500 p-4 text-neutral-700 rounded-2xl mb-6 flex space-x-4 ">
          <svg className="w-6 h-6 text-brand-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
            <path
              fill="currentColor"
              d="M48 32H197.5C214.5 32 230.7 38.74 242.7 50.75L410.7 218.7C435.7 243.7 435.7 284.3 410.7 309.3L277.3 442.7C252.3 467.7 211.7 467.7 186.7 442.7L18.75 274.7C6.743 262.7 0 246.5 0 229.5V80C0 53.49 21.49 32 48 32L48 32zM112 176C129.7 176 144 161.7 144 144C144 126.3 129.7 112 112 112C94.33 112 80 126.3 80 144C80 161.7 94.33 176 112 176z"
            />
            <path
              fill="currentColor"
              className="opacity-50"
              d="M311.1 38.93C320.5 29.61 335.7 29.7 344.1 39.13L472.8 168.4C525.1 221.4 525.1 306.6 472.8 359.6L360.8 472.9C351.5 482.3 336.3 482.4 326.9 473.1C317.4 463.8 317.4 448.6 326.7 439.1L438.6 325.9C472.5 291.6 472.5 236.4 438.6 202.1L310.9 72.87C301.5 63.44 301.6 48.25 311.1 38.93V38.93z"
            />
          </svg>
          <div>
            <span className="font-bold font-display block mb-1">Claimed Offers</span>
            <span className="block  ">{offer ? offer && offer.offer : null}</span>
            <Button
              size="small"
              className=" -ml-2 -mb-2"
              color="secondary"
              type="button"
              onClick={() => setOffer(null)}
            >
              Remove
            </Button>
          </div>
        </div>
      ) : null}
      <input type="hidden" name="_gotcha" className="!hidden" />

      {formSubmitErrors.length > 0 ? (
        <div className="shadow rounded-2xl mb-8 bg-neutral-50 p-4">
          <span className="flex mb-2 text-brand-600 font-semibold">
            <svg className="w-6 h-6 " xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>{' '}
            There were some problems with your submission.
          </span>

          <ul className="p-0 m-0 list-none">
            {formSubmitErrors.map((error, i) => (
              <li key={i} className="block">- {error}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-y-3 gap-x-3 sm:grid-cols-6">
        <div className={`${stacked ? 'sm:col-span-6' : 'sm:col-span-3'}`}>
          <label
            className={`block text-sm font-medium leading-5 text-neutral-700 mb-1 label ${
              hideLabels ? 'hidden' : ''
            } ${fontsize}`}
            htmlFor={'Firstname'}
          >
            First Name
          </label>
          <input
            className={inputClasses}
            type={'text'}
            name={'Firstname'}
            onChange={handleChange}
            id={'Firstname'}
            required={true}
            placeholder={showPlaceholders ? 'First Name' : ''}
          />
        </div>

        <div className={`${stacked ? 'sm:col-span-6' : 'sm:col-span-3'}`}>
          <label
            className={`block text-sm font-medium leading-5 text-neutral-700 mb-1 label ${
              hideLabels ? 'hidden' : ''
            } ${fontsize}`}
            htmlFor={'Lastname'}
          >
            Last Name
          </label>
          <input
            className={inputClasses}
            type={'text'}
            name={'Lastname'}
            onChange={handleChange}
            id={'Lastname'}
            required={true}
            placeholder={showPlaceholders ? 'Last Name' : ''}
          />
        </div>
        <div className={`${stacked ? 'sm:col-span-6' : 'sm:col-span-3'}`}>
          <label
            className={`block text-sm font-medium leading-5 text-neutral-700 mb-1 label ${
              hideLabels ? 'hidden' : ''
            } ${fontsize}`}
            htmlFor={'email'}
          >
            Email
          </label>
          <input
            className={inputClasses}
            type={'email'}
            name={'email'}
            onChange={handleChange}
            id={'email'}
            required={true}
            placeholder={showPlaceholders ? 'Email' : ''}
          />
        </div>

        <div className={`${stacked ? 'sm:col-span-6' : 'sm:col-span-3'}`}>
          <label
            className={`block text-sm font-medium leading-5 text-neutral-700 mb-1 label ${
              hideLabels ? 'hidden' : ''
            } ${fontsize}`}
            htmlFor={'phone'}
          >
            Phone
          </label>
          <InputMask
            placeholder={showPlaceholders ? 'Phone' : ''}
            className={inputClasses}
            required={true}
            mask="(999) 999-9999"
            type={'tel'}
            name={'phone'}
            id={'phone'}
            onChange={handleChange}
            value={formInputStates && formInputStates.phone ? formInputStates.phone : null}
          />
        </div>
        <div className="sm:col-span-6">
          <label
            className={`block text-sm font-medium leading-5 text-neutral-700 mb-1 label ${
              hideLabels ? 'hidden' : ''
            } ${fontsize}`}
            htmlFor={'description'}
          >
            Tell us about your project
          </label>
          <textarea
            className={`${inputClasses} textarea`}
            name={'description'}
            rows={5}
            onChange={handleChange}
            id={'description'}
            required={true}
            placeholder={showPlaceholders ? 'Tell us about your project' : ''}
          />
        </div>
      </div>
      <div className="field">
        <Button
          className="w-full mt-2 md:text-base cursor-pointer font-semibold px-4 py-3 lg:py-3 lg:px-5 rounded-2xl text-white font-display flex items-center justify-center"
          type="submit"
          disabled={submitInProgress}
        >
          Request FREE Estimate <ArrowRight size={24} className="ml-2" color={'#fff'} strokeWidth={2} />
          {submitInProgress ? (
            <svg
              className="animate-spin ml-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          ) : null}{' '}
        </Button>
      </div>

      <div className={`my-3`}>
        <p className="text-neutral-500 text-center text-xs flex items-center justify-center pb-2">
          {' '}
          <svg
            className="w-4 h-4 mr-1 text-neutral-400 flex-shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
              clipRule="evenodd"
            />
          </svg>{' '}
          Your privacy is important to us.
        </p>
      </div>
    </form>
  );
}

export default ContactForm;
